import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

// Plans and what they cost.
//
// Limits (daily reveals, rows per upload) live in the plan_limits table
// (migration 031) and are read from there, so changing a limit is one row
// edit with no release. The marketing copy below (prices, the first-month
// offer, what's included) is what the pricing page says; keep it in step with
// the website.

/**
 * Payments are switched off until Stripe is connected. When it is, set
 * REACT_APP_BILLING_ENABLED=true and deploy the create-checkout function; the
 * Pay buttons then go live with no other change.
 */
export const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED === "true";

// Top-up credits: $5 per 1,000, bought in steps of 1,000 (so $5 minimum).
export const CREDIT_PACK = { credits: 1000, priceUsd: 5 };
export const TOPUP_OPTIONS = [1000, 2000, 5000, 10000, 25000];
export const topupPrice = (credits) => (credits / CREDIT_PACK.credits) * CREDIT_PACK.priceUsd;

export const PLANS = [
  {
    id: "starter", label: "Starter", credits: 5000,
    monthly: 29, firstMonth: 4, weCover: 25, annual: 23,
    blurb: "For a founder or one rep doing outbound.",
    features: ["5,000 credits every month", "Unused credits roll over", "Chrome extension", "Enrich CSV uploads"],
  },
  {
    id: "growth", label: "Growth", credits: 10000, featured: true,
    monthly: 49, firstMonth: 4, weCover: 45, annual: 39,
    blurb: "For a small sales team that lives in outbound.",
    features: ["10,000 credits every month", "Unused credits roll over", "Chrome extension", "Enrich CSV uploads", "Higher daily reveal limit"],
  },
  {
    id: "scale", label: "Scale", credits: 50000,
    monthly: 149, firstMonth: 49, weCover: 100, annual: 119,
    blurb: "For agencies and teams at volume.",
    features: ["50,000 credits every month", "Unused credits roll over", "Chrome extension", "Enrich CSV uploads", "Highest daily reveal limit"],
  },
];

export const PLAN_LABELS = { free: "Free", starter: "Starter", growth: "Growth", scale: "Scale", enterprise: "Enterprise" };

export const planLabel = (plan) => PLAN_LABELS[plan] || plan || "Free";
export const isPaidPlan = (plan) => Boolean(plan) && plan !== "free";

// Fallback limits for when plan_limits has not been created yet (migration
// 031 not applied). The server enforces the real ones either way.
const FALLBACK_LIMITS = {
  free: { daily_reveal_limit: 100, max_rows_per_job: 10000, monthly_credits: 100 },
  starter: { daily_reveal_limit: 1000, max_rows_per_job: 10000, monthly_credits: 5000 },
  growth: { daily_reveal_limit: 2500, max_rows_per_job: 10000, monthly_credits: 10000 },
  scale: { daily_reveal_limit: 7500, max_rows_per_job: 10000, monthly_credits: 50000 },
  enterprise: { daily_reveal_limit: 7500, max_rows_per_job: 10000, monthly_credits: 100000 },
};

let limitsPromise = null;
/** plan → { daily_reveal_limit, max_rows_per_job, monthly_credits }, cached per page load. */
export function getPlanLimits() {
  if (!limitsPromise) {
    limitsPromise = supabase
      .from("plan_limits")
      .select("plan, daily_reveal_limit, max_rows_per_job, monthly_credits")
      .then(({ data, error }) => {
        if (error || !data?.length) return FALLBACK_LIMITS;
        return { ...FALLBACK_LIMITS, ...Object.fromEntries(data.map((r) => [r.plan, r])) };
      })
      .catch(() => FALLBACK_LIMITS);
  }
  return limitsPromise;
}

export function usePlanLimits(plan) {
  const [limits, setLimits] = useState(FALLBACK_LIMITS[plan || "free"] || FALLBACK_LIMITS.free);
  useEffect(() => {
    let active = true;
    getPlanLimits().then((all) => active && setLimits(all[plan || "free"] || all.free));
    return () => { active = false; };
  }, [plan]);
  return limits;
}

export const formatNumber = (n) => Number(n || 0).toLocaleString("en-US");

/**
 * Send the person to Stripe Checkout for a plan. Only called when
 * BILLING_ENABLED; the create-checkout Edge Function returns the Stripe URL.
 */
export async function startCheckout({ plan, interval = "monthly", offer, topupCredits }) {
  const { data } = await supabase.auth.getSession();
  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${data?.session?.access_token}` },
    body: JSON.stringify({ plan, interval, offer, topup_credits: topupCredits, return_url: `${window.location.origin}/billing/result` }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.url) throw new Error(body.error || "Checkout is not available right now.");
  window.location.assign(body.url);
}
